import React, { useEffect, useState } from 'react';
import { Timeline } from '@mui/lab';
import styled from '@emotion/styled';
import { Alert, Badge, BadgeProps, Button, IconButton } from '@mui/material';
import axios from '@/_utils/axios';
import { selectCurrentTableKey, selectShouldGetData } from '@/redux/selectors/dataTableSelectors';
import { getData } from '@/redux/slices/dataTableSlice';
import PolarisIcon from '@/components/_common/PolarisIcon';
import Modal from '@/components/_common/Modal/Modal';
import Spinner from '@/components/_common/Spinner';
import {
  IngestFile,
  OrderJobAsset,
  OrderJobTask,
  Note as NoteType,
  JobDetail,
  FocusVersionScheduling,
  OrderJob,
  Exception
} from '@/redux/slices/slice_types';
import {
  selectCanChangeExceptions,
  selectCanWriteIngestRecord,
  selectCanWriteJobs,
  selectCanWritePendingAssets,
  selectCanWriteTasks
} from '@/redux/selectors/authSelectors';
import { DataTableConfig } from '@/__configs/config_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import Note from './Note';
import NoteReply from './NoteReply';

interface NotesProps {
  data: OrderJob | OrderJobTask | OrderJobAsset | IngestFile | JobDetail | FocusVersionScheduling | Exception;
  type?: keyof DataTableConfig;
  className?: string;
  badgeColor?: string;
  override?: boolean; // pivots data off type instead of currentTableKey
}

type EnabledNotesTable = {
  url: string;
  type: string;
  permission?: any;
};

type StrictExtract<T, U extends T> = U;
type PageWithNotes = StrictExtract<
  keyof DataTableConfig,
  'jobs' | 'pendingAssets' | 'tasks' | 'jobDetailTasks' | 'fileIngest' | 'focusSchedule' | 'exceptions' | 'jobDetailBundleJobs'
>;

const enabledNotesTables: Record<PageWithNotes, EnabledNotesTable> = {
  jobs: {
    url: 'core/jobs',
    type: 'job',
    permission: selectCanWriteJobs
  },
  pendingAssets: {
    url: 'core/jobs/assets',
    type: 'asset',
    permission: selectCanWritePendingAssets
  },
  tasks: {
    url: 'core/tasks',
    type: 'task',
    permission: selectCanWriteTasks
  },
  jobDetailTasks: {
    url: 'core/tasks',
    type: 'task',
    permission: selectCanWriteTasks
  },
  fileIngest: {
    url: 'ingest_manager/ingest_records',
    type: 'file',
    permission: selectCanWriteIngestRecord
  },
  focusSchedule: {
    url: 'focus/scheduling',
    type: 'schedule',
    permission: selectCanWriteJobs
  },
  exceptions: {
    url: 'monitoring/events',
    type: 'exception',
    permission: selectCanChangeExceptions
  },
  jobDetailBundleJobs: {
    url: 'core/jobs',
    type: 'job',
    permission: selectCanWriteJobs
  }
};

const isTableHasNotes = (currentTable: keyof DataTableConfig): currentTable is PageWithNotes => {
  return currentTable in enabledNotesTables;
};

const Notes = ({ data, className, badgeColor = 'error', type = 'tasks', override = false }: NotesProps) => {
  const dispatch = useAppDispatch();
  const shouldRefreshData = useAppSelector(selectShouldGetData);
  const [modalOpen, setModalOpen] = useState(false);
  const [notes, setNotes] = useState<NoteType[]>([]);
  const [pagination, setPagination] = useState<{ total: number }>({
    total: 0
  });
  const [page, setPage] = useState(0);
  const unreadCommentsCount = data && 'unread_comments_count' in data ? data.unread_comments_count || 0 : 0;
  const [showBadge, setShowBadge] = useState(unreadCommentsCount > 0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const currentTable: keyof DataTableConfig = override ? type : (currentTableKey ?? type);
  const url = isTableHasNotes(currentTable) ? enabledNotesTables[currentTable]?.url : enabledNotesTables.tasks.url;
  const permissionSelector = isTableHasNotes(currentTable) ? enabledNotesTables[currentTable]?.permission : () => false;
  const hasPermissions = useAppSelector(permissionSelector);

  useEffect(() => {
    if (modalOpen) loadNotes();
  }, [page, modalOpen]);

  useEffect(() => {
    setShowBadge((unreadCommentsCount || 0) > 0);
  }, [data?.id, unreadCommentsCount]);

  const loadNotes = () => {
    setError(false);
    setLoading(true);
    axios
      .post(`/${url}/${data?.id || data?.hid}/comments/?page_size=6&page_num=${page}`, {
        direction: 'desc',
        order_on: 'created_at'
      })
      .then((res) => {
        setNotes([...(res.data?.data?.reverse() ?? []), ...notes]);
        setPagination(res.data?.pagination);
        setShowBadge(false);
      })
      .catch(() => setError(true))
      .finally(() => {
        setLoading(false);
      });
  };

  const addNewNote = (newNote: NoteType) => {
    setNotes([...notes, newNote]);
  };

  const loadOlderNotes = () => {
    setPage(page + 1);
  };

  const closeModal = () => {
    setModalOpen(false);
    setPagination({ total: 0 });
    setPage(0);
    setNotes([]);

    // only get data again if we need it. job detail related tasks have
    // `disableGetData` set to true bc tasks are already returned on job
    if (shouldRefreshData) {
      dispatch(getData() as any);
    }
  };

  const renderAlert = () => {
    if (!loading && isTableHasNotes(currentTable)) {
      return (
        <Alert sx={{ mx: 'auto', my: 'auto', mb: 3.5, maxWidth: 400 }} severity={error ? 'error' : 'info'}>
          {error
            ? `There was a problem retrieving ${enabledNotesTables[currentTable].type} notes.`
            : `No ${enabledNotesTables?.[currentTable]?.type} notes found.`}
        </Alert>
      );
    }
  };

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  return (
    <div key={String(modalOpen)} onClick={(e) => e.stopPropagation()} className={className}>
      <IconButton data-testid="modal-open-btn" color="primary" onClick={handleIconClick}>
        <StyledBadge
          badgeContent={unreadCommentsCount}
          color={badgeColor as BadgeProps['color']}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          invisible={!showBadge}
        >
          <StyledNoteIcon icon="comment" height={24} />
        </StyledBadge>
      </IconButton>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={
          <>
            Notes: <strong>{data?.hid || data.id}</strong>
          </>
        }
        icon={<PolarisIcon icon="comment" height={20} width={20} />}
      >
        <div>
          {loading && <Spinner />}
          <StyledLoadingDiv loading={String(loading)}>
            {pagination.total > notes.length && (
              <StyledViewOlderDiv onClick={loadOlderNotes}>
                <Button size="small">View older notes</Button>
              </StyledViewOlderDiv>
            )}
            <StyledTimeline placeholder={undefined}>
              {notes.length ? notes.map((note) => <Note note={note} key={note?.id} />) : renderAlert()}
              {!!hasPermissions && (
                <NoteReply taskId={String(data?.id || data?.hid)} addNewNote={addNewNote} firstNote={notes.length === 0} baseUrl={url} />
              )}
            </StyledTimeline>
          </StyledLoadingDiv>
        </div>
      </Modal>
    </div>
  );
};

export default Notes;

const StyledTimeline = styled(Timeline)`
  padding: ${(props) => props.theme.spacing(0, 1)};

  ${(props) => `${props.theme.breakpoints.down('sm')} {
    padding: ${props.theme.spacing(2)};
  }`};

  & > .MuiTimelineItem-root:last-of-type {
    & .MuiTimelineConnector-root {
      opacity: 0.2;
    }
  }

  & .MuiTimelineItem-root::before {
    display: none;
  }
`;

const StyledNoteIcon = styled(PolarisIcon)`
  margin: ${(props) => props.theme.spacing(-0.5)};
`;

const StyledViewOlderDiv = styled.div`
  margin-top: ${(props) => props.theme.spacing(1)};
  display: flex;
  white-space: nowrap;
  margin-left: 240px;
  margin-bottom: ${(props) => props.theme.spacing(-2)};

  & .MuiButton-root {
    font-size: 0.85rem;
  }
`;

const StyledLoadingDiv = styled.div<{ loading?: string }>`
  ${(props) =>
    props.loading === String(true)
      ? `
    pointer-events: none;
    opacity: 0.2;
  `
      : null};
`;

const StyledBadge = styled(Badge)`
  & .MuiBadge-badge {
    width: 16px;
    min-width: 16px;
    height: 16px;
    top: -5px;
    right: -5px;
    text-wrap: nowrap;
  }
`;
